import type { BreadcrumbLinkType } from '@/components/atoms/BreadcrumbLink'
import type { CardArticleType } from '@/components/atoms/CardArticle'
import type { CardFeatureProps } from '@/components/atoms/CardFeature'
import type {
	IconData,
	IconLinkType,
	IconType,
	ImageData,
	LinkType,
	Maybe,
	MegaMenuType,
	MenuItemType,
	VideoData,
} from '@/frontend-types'
import type {
	Document,
	FontAwesomeIconPrefix,
	Icon,
	Image,
	Menu,
	Page,
	PayloadLinkFields,
	VenueStatus,
	Video,
} from '@/payload-types'
import type { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core'
import type { LinkFields } from '@payloadcms/richtext-lexical'
import type { TreeItem } from 'payloadcms-plugin-menu-builder'
import { treeMenuSchema } from 'payloadcms-plugin-menu-builder'
import { getPageUrl } from './breadcrumbs'
import { getTimeAgo } from './date'
import { populated } from './populated'

function transformSearchParams(params: string) {
	if (!params) return ''
	try {
		return '?' + new URLSearchParams(params).toString()
	} catch {
		return ''
	}
}

function transformAnchor(anchor: string) {
	if (!anchor) return ''
	return '#' + encodeURIComponent(anchor.toString())
}

function resolveNodeHref(node: TreeItem) {
	switch (node.data.type) {
		case 'collection': {
			let url = node.data.url || ''
			if (node.data.query) url += transformSearchParams(node.data.query)
			if (node.data.anchor) url += transformAnchor(node.data.anchor)
			return url
		}
		default:
			return node.data.url
	}
}

function transformMenuNodes(nodes: TreeItem[]): MenuItemType[] {
	return nodes.map((node) => ({
		title: node.meta.customLabel || node.data.title,
		href: resolveNodeHref(node),
		description: node.meta.description,
		target: node.meta.newTab ? '_blank' : '_self',
		children: transformMenuNodes((node.children ?? []) as TreeItem[]),
	}))
}

export function transformMenu(menu: Maybe<string | Menu>): MegaMenuType | undefined {
	if (!populated(menu)) return undefined
	try {
		const parsed = treeMenuSchema.parse(menu.structure)
		const title = menu.title
		const menuItems = transformMenuNodes(parsed)
		return { title, menuItems }
	} catch {
		return undefined
	}
}

export function internalLinkToHref(fields: LinkFields | PayloadLinkFields) {
	const { doc, query, anchor } = fields
	switch (doc?.relationTo) {
		case 'pages': {
			let url = populated(doc.value) ? getPageUrl(doc.value as Page) : ''
			if (typeof query === 'string') url += transformSearchParams(query)
			if (typeof anchor === 'string') url += transformAnchor(anchor)
			return url
		}
		case 'documents':
			return populated(doc.value) ? (doc.value as Document).url || '' : ''
		default:
			return ''
	}
}

export function linkFieldsToHref(fields: LinkFields | PayloadLinkFields) {
	switch (fields.linkType) {
		case 'internal':
			return internalLinkToHref(fields)
		case 'custom':
			return fields.url ?? ''
		default:
			return ''
	}
}

export function linkIsDownload(fields: LinkFields | PayloadLinkFields): boolean {
	return fields.linkType === 'internal' && fields.doc?.relationTo === 'documents'
}

function externalLinkToIcon(href: Maybe<string>): IconType {
	const [scheme] = href?.split(':') ?? []
	switch (scheme) {
		case 'mailto':
			return ['fas', 'envelope']
		case 'tel':
			return ['fas', 'phone']
		default:
			return ['fas', 'external-link']
	}
}

export function linkFieldsToIcon(fields: LinkFields | PayloadLinkFields): IconType {
	switch (fields.linkType) {
		case 'custom':
			return externalLinkToIcon(fields.url)
		default:
			switch (fields.doc?.relationTo) {
				case 'documents':
					return ['fas', 'arrow-down-to-line']
				default:
					return ['fas', 'chevron-right']
			}
	}
}

export function linkTypeToIcon(link: LinkType): IconType {
	if (link.download) {
		return ['fas', 'arrow-down-to-line']
	}
	if (link.href.startsWith('/')) {
		return ['fas', 'chevron-right']
	}
	return externalLinkToIcon(link.href)
}

export function linkFieldsToLinkType(fields: PayloadLinkFields): LinkType {
	return {
		href: linkFieldsToHref(fields),
		label: fields.text,
		target: fields.newTab ? '_blank' : '_self',
		download: linkIsDownload(fields),
	}
}

export function transformSocialLinks(
	socialLinks:
		| {
				href: string
				faPrefix: FontAwesomeIconPrefix
				faIcon: string
				id?: string | null
		  }[]
		| null
		| undefined,
): IconLinkType[] | undefined {
	if (!socialLinks) return undefined
	try {
		const iconLinks: IconLinkType[] = socialLinks.map((item) => ({
			label: item.faIcon,
			href: item.href,
			target: '_blank',
			icon: [item.faPrefix as IconPrefix, item.faIcon as IconName],
		}))
		return iconLinks
	} catch {
		return undefined
	}
}

export function transformImage(image: Maybe<string | Image>): ImageData | undefined {
	if (!populated(image)) return undefined
	if (!image.url) return undefined
	return {
		src: image.url,
		alt: image.alt,
		width: image.width ?? 0,
		height: image.height ?? 0,
	}
}

export function transformIcon(icon: Maybe<string | Icon>): IconData | undefined {
	if (!populated(icon)) return undefined
	if (!icon.url) return undefined
	return {
		src: icon.url,
		width: icon.width ?? 0,
		height: icon.height ?? 0,
	}
}

export function transformImages(images: Maybe<(string | Image)[]>): ImageData[] {
	if (!images) return []
	return images.reduce<ImageData[]>((acc, image) => {
		const transformedImage = transformImage(image)
		if (transformedImage) {
			acc.push(transformedImage)
		}
		return acc
	}, [])
}

export type TransformBreadcrumbsOptions = {
	omitLastItem?: boolean
}

export function transformBreadcrumbs(
	doc: Page,
	options?: TransformBreadcrumbsOptions,
): BreadcrumbLinkType[] {
	const items = options?.omitLastItem ? doc.breadcrumbs?.slice(0, -1) : doc.breadcrumbs
	return (
		items?.map<Pick<BreadcrumbLinkType, 'href' | 'label'>>((breadcrumb) => ({
			href: breadcrumb.url,
			label: breadcrumb.label,
		})) ?? []
	)
}

export function transformVideo(video: Maybe<string | Video>): VideoData | undefined {
	if (!populated(video) || !video.url) return undefined

	return {
		src: video.url,
		title: video.attribution,
	}
}

export function getPageTitle(page: Maybe<string | Page>) {
	return populated(page) ? page?.title : undefined
}

export function transformNewsArticleToCard(
	article: Maybe<string | Page>,
	categoryPage?: Maybe<string | Page>,
): CardArticleType | undefined {
	if (!populated(article) || !article.newsArticle) {
		return undefined
	}

	const { heroImage, publishDate } = article.newsArticle
	return {
		slug: article.slug,
		title: article.title,
		category: getPageTitle(article.parent) || getPageTitle(categoryPage),
		image: transformImage(heroImage),
		date: getTimeAgo(publishDate),
		url: getPageUrl(article),
	}
}

export function transformNewsArticlesToCards(
	articles: Maybe<(string | Page)[]>,
	categoryPage?: Maybe<string | Page>,
) {
	const cards: CardArticleType[] = []
	for (const article of articles || []) {
		const articleData = transformNewsArticleToCard(article, categoryPage)
		if (articleData) cards.push(articleData)
	}
	return cards
}

export function transformNewsArticleToFeature(
	article: Maybe<string | Page>,
	categoryPage?: Maybe<string | Page>,
): CardFeatureProps | undefined {
	if (!populated(article) || !article.newsArticle) return undefined

	const { heroImage, publishDate, excerpt } = article.newsArticle
	const category = getPageTitle(article.parent) || getPageTitle(categoryPage)

	return {
		title: article.title,
		category,
		excerpt,
		images: heroImage ? transformImages([heroImage]) : undefined,
		date: getTimeAgo(publishDate),
		url: getPageUrl(article),
		type: 'news',
	}
}

export function transformComponentsToLink(
	label: Maybe<string>,
	href: Maybe<string>,
	target: '_self' | '_blank' = '_self',
): LinkType | undefined {
	return label && href ? { label, href, target } : undefined
}

export function transformPageToLink(
	page: Maybe<string | Page>,
	overrideTitle?: string,
): LinkType | undefined {
	return populated(page)
		? transformComponentsToLink(overrideTitle ?? page.title, getPageUrl(page))
		: undefined
}

const statusMap: Record<VenueStatus, string> = {
	new: 'New',
	upgraded: 'Upgraded',
}

export function transformVenueStatus(status: VenueStatus): string {
	return statusMap[status]
}
